import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import ClientLayout from "../components/ClientLayout";
import { getPurchaseOrders } from "../api/clientPortal";
import { useSnack } from "../services/SnakbarProvider";
import { handleError } from "../services/errorHandler";

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const DetailStackCell = ({ items, getLabel }) => {
  if (!items?.length) return "-";
  return (
    <Stack
      direction="column"
      spacing={0.5}
      justifyContent="center"
      sx={{ height: "100%", py: 0.5 }}
    >
      {items.map((item) => {
        const label = getLabel(item);
        if (!label) return null;
        return (
          <Typography
            key={item.id ?? label}
            variant="body2"
            component="div"
            sx={{ lineHeight: "28px", minHeight: 28 }}
          >
            {label}
          </Typography>
        );
      })}
    </Stack>
  );
};

const ProgressBar = ({ shipped, total }) => {
  const progress = total > 0 ? (shipped / total) * 100 : 0;
  return (
    <Box
      sx={{
        width: "100%",
        height: 20,
        bgcolor: "#edf4fb",
        borderRadius: 1,
        position: "relative",
        overflow: "hidden",
        minHeight: 28,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: `${Math.min(progress, 100)}%`,
          height: "100%",
          bgcolor: "#a5c8f0",
          transition: "width .5s ease-in-out",
          position: "absolute",
          left: 0,
          top: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          position: "relative",
          width: "100%",
          textAlign: "center",
          color: "#1976d2",
          fontWeight: "bold",
          textShadow: "0 0 2px white",
          zIndex: 1,
        }}
      >
        {`${shipped}/${total}`}
      </Typography>
    </Box>
  );
};

const columns = [
  {
    field: "clientName",
    headerName: "Cliente",
    flex: 0.2,
  },
  {
    field: "code",
    headerName: "PO",
    flex: 0.15,
  },
  {
    field: "finishedGoodDescription",
    headerName: "Producto",
    flex: 0.25,
    sortable: false,
    renderCell: (params) => (
      <DetailStackCell
        items={params.row.details}
        getLabel={(d) =>
          d.finishedGoodDescription || d.finishedGoodName || null
        }
      />
    ),
  },
  {
    field: "finishedGoodCode",
    headerName: "Clave",
    flex: 0.15,
    sortable: false,
    renderCell: (params) => (
      <DetailStackCell
        items={params.row.details}
        getLabel={(d) => d.finishedGoodCode || null}
      />
    ),
  },
  {
    field: "currentOperationStatus",
    headerName: "Estatus",
    flex: 0.15,
    renderCell: (params) =>
      params.value ? (
        <Chip
          size="small"
          label={params.value}
          sx={
            params.row.currentOperationStatusColor
              ? {
                  bgcolor: params.row.currentOperationStatusColor,
                  color: "text.primary",
                }
              : undefined
          }
        />
      ) : (
        "-"
      ),
  },
  {
    field: "lastMad",
    headerName: "Last MAD",
    flex: 0.15,
    valueFormatter: (value) => formatDate(value),
  },
  {
    field: "progress",
    headerName: "Progreso",
    flex: 0.2,
    sortable: false,
    renderCell: (params) => {
      const details = params.row.details || [];
      if (details.length === 0) {
        const shipped = params.row.shippedQuantity ?? 0;
        const total = params.row.quantity ?? 0;
        return <ProgressBar shipped={shipped} total={total} />;
      }
      return (
        <Stack
          spacing={0.5}
          justifyContent="center"
          sx={{ width: "100%", py: 0.5 }}
        >
          {details.map((d) => (
            <ProgressBar
              key={d.id ?? `${d.finishedGoodCode}-${d.quantity}`}
              shipped={d.shippedQuantity ?? 0}
              total={d.quantity ?? 0}
            />
          ))}
        </Stack>
      );
    },
  },
];

export default function PurchaseOrdersPage() {
  const { openSnack } = useSnack();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const isFirstSearchEffect = useRef(true);

  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { pageSize: 1000, showCompleted };
        if (debouncedSearch) {
          params.search = debouncedSearch;
        }
        const res = await getPurchaseOrders(params);
        setRows(res.data ?? []);
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [debouncedSearch, showCompleted]);

  const toolbar = useMemo(
    () => (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Buscar..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "action.active" }} />
            ),
          }}
          sx={{ backgroundColor: "background.paper", maxWidth: 400, flex: 1 }}
        />
        <Button onClick={() => setShowCompleted((prev) => !prev)}>
          {showCompleted ? "Ocultar completados" : "Mostrar completados"}
        </Button>
      </Box>
    ),
    [searchValue, showCompleted]
  );

  return (
    <ClientLayout>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Mis órdenes de compra
      </Typography>
      {toolbar}
      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          onRowClick={(params) => navigate(`/orders/${params.id}`)}
          getRowHeight={() => "auto"}
          sx={{
            cursor: "pointer",
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              py: 1,
            },
          }}
        />
      </Box>
    </ClientLayout>
  );
}
